import { X as XIcon } from 'react-feather';
import { Box, Divider, IconButton, Modal, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { LeavingWithoutSavingPrompt } from '~/modules/@shared/components';
import useTakePaymentStore from '../../hooks/useTakePaymentStore';
import { TakePaymentForm } from './components/TakePaymentForm';
import { TakePaymentFormData, takePaymentFormDefault, takePaymentFormResolver } from '../../helpers/TakePaymentSchema';
import { ProcessChargeButton } from './components/ProcessChargeButton';
import { CancelPaymentButton } from './components/CancelPaymentButton';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useGetPaymentV2Query } from '~/services/member';
import { useLocationContext } from '~/contexts/location.context';
import { useGetIds } from '~/modules/auth';

export default function TakePaymentModal() {
  const { locationId, isDea } = useLocationContext();
  const { memberId } = useGetIds();
  const isOpen = useTakePaymentStore((state) => state.isOpen);
  const clean = useTakePaymentStore((state) => state.clean);
  const contactId = useTakePaymentStore((state) => state.contactId);
  const paymentId = useTakePaymentStore((state) => state.paymentId);
  const amount = useTakePaymentStore((state) => state.amount);

  const { data: paymentDetail } = useGetPaymentV2Query({
    locationId,
    memberId: contactId,
    paymentId,
    execute: true,
  });

  const formValues = useMemo<TakePaymentFormData | undefined>(() => {
    if (isDea) {
      return {
        isDea: true as const,
        member_id: paymentDetail?.member_id ?? memberId,
        payment_method_id: paymentDetail?.payment_method_id ?? 0,
        amount: amount ?? paymentDetail?.amount ?? 0,
        scheduled_date: paymentDetail?.scheduled_date || dayjs().format('YYYY-MM-DD'),
        description: '',
        taxable: paymentDetail?.tax !== 0,
        payment_id: paymentDetail?.payment_id,
      };
    }

    return undefined;
  }, [paymentDetail, isDea, amount, memberId]);

  const methods = useForm<TakePaymentFormData>({
    resolver: takePaymentFormResolver,
    defaultValues: takePaymentFormDefault,
    values: formValues,
  });

  const { reset, setValue } = methods;

  const handleModalCancel = () => {
    reset();
    clean();
  };

  useEffect(() => {
    if (isOpen && contactId) {
      setValue('member_id', contactId);
    }
  }, [isOpen, contactId, setValue]);

  return (
    <Modal
      open={isOpen}
      onClose={handleModalCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <FormProvider {...methods}>
        <LeavingWithoutSavingPrompt />
        <Box
          sx={{
            borderRadius: '10px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'rgb(246, 247, 251)',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
            <Typography>Take a payment</Typography>
            <IconButton onClick={handleModalCancel}>
              <XIcon size={16} />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 2, maxHeight: 'calc(95dvh - 200px)', overflowY: 'auto' }}>
            <TakePaymentForm />
          </Box>
          <Divider />
          <Box sx={{ pt: 3, pb: 3, pr: 3, pl: 3, display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
            <CancelPaymentButton />
            <ProcessChargeButton />
          </Box>
        </Box>
      </FormProvider>
    </Modal>
  );
}
